import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { repairMojibakeDeep } from "../lib/text";

const copyMap = {
  ar: {
    eyebrow: "لوحة المتابعة",
    title: "ملخص سريع للمدرب",
    subtitle: "صفحة خفيفة للمقارنة السريعة بين القبلي والبعدي فقط. تفاصيل الطلبة في صفحة النتائج، والرسوم الكاملة في صفحة التحليلات.",
    course: "الكورس",
    mode: "نوع العرض",
    pre: "قبلي",
    post: "بعدي",
    both: "الاثنان معًا",
    chooseCourse: "اختر كورسًا",
    loading: "جارٍ تحميل البيانات...",
    noCourses: "لا توجد كورسات بعد.",
    noData: "لا توجد بيانات كافية لهذا الجزء حتى الآن.",
    loadError: "تعذر تحميل البيانات الآن. تأكد من تشغيل الباك ثم أعد المحاولة.",
    openResults: "فتح صفحة النتائج والطلبة",
    openAnalytics: "فتح صفحة التحليلات",
    completeComparisons: "مقارنات مكتملة",
    averagePre: "متوسط القبلي",
    averagePost: "متوسط البعدي",
    averageGain: "متوسط التحسن",
    improvementRate: "نسبة التحسن",
    improved: "المتحسنون",
    declined: "المتراجعون",
    unchanged: "الثابتون",
    metricsTitle: "ملخص المقارنة",
    comparisonAverages: "متوسطات القبلي والبعدي",
    comparisonOutcomes: "نتيجة المقارنة",
    quickAnalysis: "تحليل سريع",
    quickSummary: "ملخص سريع",
    aiByline: "تم بوساطة الذكاء الاصطناعي",
    strengths: "نقاط القوة",
    risks: "ما يحتاج متابعة",
    recommendations: "توصيات سريعة",
    ageInsightPrefix: "أوضح تحسن عمري ظهر في",
    governorateInsightPrefix: "أوضح تحسن جغرافي ظهر في"
  },
  en: {
    eyebrow: "Overview",
    title: "Quick Trainer Snapshot",
    subtitle: "A lightweight page for quick pre/post comparison only. Learner details stay in results, and deeper charts stay in analytics.",
    course: "Course",
    mode: "View Mode",
    pre: "Pre",
    post: "Post",
    both: "Both",
    chooseCourse: "Choose a course",
    loading: "Loading data...",
    noCourses: "There are no courses yet.",
    noData: "There is not enough data for this section yet.",
    loadError: "Unable to load data right now. Make sure the backend is running and try again.",
    openResults: "Open Results And Learners",
    openAnalytics: "Open Analytics",
    completeComparisons: "Complete Comparisons",
    averagePre: "Average Pre",
    averagePost: "Average Post",
    averageGain: "Average Gain",
    improvementRate: "Improvement Rate",
    improved: "Improved",
    declined: "Declined",
    unchanged: "Unchanged",
    metricsTitle: "Comparison Summary",
    comparisonAverages: "Pre vs Post Averages",
    comparisonOutcomes: "Comparison Outcome",
    quickAnalysis: "Quick Analysis",
    quickSummary: "Quick Summary",
    aiByline: "Generated with AI",
    strengths: "Strengths",
    risks: "Needs Attention",
    recommendations: "Quick Recommendations",
    ageInsightPrefix: "The clearest age-based improvement appeared in",
    governorateInsightPrefix: "The clearest location-based improvement appeared in"
  }
};

function formatNumber(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2);
}

function TrainerOverviewPage() {
  const user = getStoredUser();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === "ar";
  const copy = repairMojibakeDeep(copyMap[isArabic ? "ar" : "en"]);

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedMode, setSelectedMode] = useState("both");
  const [dashboardData, setDashboardData] = useState(null);
  const [courseReport, setCourseReport] = useState(null);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    let active = true;

    async function fetchCourses() {
      if (!user?.id) {
        setIsLoadingCourses(false);
        return;
      }

      try {
        setPageError("");
        const response = await api.get(`/trainer/${user.id}/courses`);
        if (!active) {
          return;
        }
        const nextCourses = repairMojibakeDeep(response.data.courses || []);
        setCourses(nextCourses);
        if (nextCourses.length) {
          setSelectedCourseId(String(nextCourses[0].id));
        }
      } catch (error) {
        if (!active) {
          return;
        }
        setCourses([]);
        setPageError(error.response?.data?.error || copy.loadError);
      } finally {
        if (active) {
          setIsLoadingCourses(false);
        }
      }
    }

    fetchCourses();
    return () => {
      active = false;
    };
  }, [copy.loadError, user?.id]);

  useEffect(() => {
    let active = true;

    async function fetchDashboard() {
      if (!user?.id || !selectedCourseId) {
        setDashboardData(null);
        return;
      }

      try {
        setPageError("");
        const response = await api.get(`/courses/${selectedCourseId}/dashboard-overview`, {
          params: {
            trainer_id: user.id,
            mode: selectedMode
          }
        });
        if (active) {
          setDashboardData(repairMojibakeDeep(response.data));
        }
      } catch (error) {
        if (!active) {
          return;
        }
        setDashboardData(null);
        setPageError(error.response?.data?.error || copy.loadError);
      }
    }

    fetchDashboard();
    return () => {
      active = false;
    };
  }, [copy.loadError, selectedCourseId, selectedMode, user?.id]);

  useEffect(() => {
    let active = true;

    async function fetchCourseReport() {
      if (!user?.id || !selectedCourseId) {
        setCourseReport(null);
        return;
      }

      try {
        setIsLoadingReports(true);
        const response = await api.get(`/courses/${selectedCourseId}/ai-report`, {
          params: { trainer_id: user.id }
        });
        if (active) {
          setCourseReport(repairMojibakeDeep(response.data.report || null));
        }
      } catch (error) {
        if (active) {
          setCourseReport(null);
        }
      } finally {
        if (active) {
          setIsLoadingReports(false);
        }
      }
    }

    fetchCourseReport();
    return () => {
      active = false;
    };
  }, [selectedCourseId, user?.id]);

  const selectedCourse = useMemo(
    () => courses.find((course) => String(course.id) === String(selectedCourseId)) || null,
    [courses, selectedCourseId]
  );

  const comparison = useMemo(() => dashboardData?.comparison || {}, [dashboardData]);
  const improvementRate = useMemo(() => {
    const totalCompared = Number(comparison.compared_students || 0);
    if (!totalCompared) {
      return 0;
    }
    return Number((((comparison.improved_students || 0) / totalCompared) * 100).toFixed(1));
  }, [comparison]);

  const comparisonAverageChart = useMemo(
    () => [
      {
        name: copy.comparisonAverages,
        pre: Number(comparison.average_pre_score || 0),
        post: Number(comparison.average_post_score || 0)
      }
    ],
    [comparison.average_post_score, comparison.average_pre_score, copy.comparisonAverages]
  );

  const comparisonOutcomeChart = useMemo(
    () => [
      {
        name: copy.comparisonOutcomes,
        improved: Number(comparison.improved_students || 0),
        unchanged: Number(comparison.unchanged_students || 0),
        declined: Number(comparison.declined_students || 0)
      }
    ],
    [comparison.declined_students, comparison.improved_students, comparison.unchanged_students, copy.comparisonOutcomes]
  );

  const bestAgeGroup = useMemo(
    () => (dashboardData?.improvement_by_age || []).slice().sort((a, b) => b.average_gain - a.average_gain)[0] || null,
    [dashboardData?.improvement_by_age]
  );

  const bestGovernorate = useMemo(
    () => (dashboardData?.improvement_by_governorate || []).slice().sort((a, b) => b.average_gain - a.average_gain)[0] || null,
    [dashboardData?.improvement_by_governorate]
  );

  return (
    <div className="workspace-page">
      <section className="hero-panel trainer-overview-hero">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
        <div className="action-cluster">
          <button className="primary-button" type="button" onClick={() => navigate("/trainer-results")}>
            {copy.openResults}
          </button>
          <button className="ghost-button" type="button" onClick={() => navigate("/trainer-analytics")}>
            {copy.openAnalytics}
          </button>
        </div>
      </section>

      <section className="panel-card">
        <div className="two-column-grid">
          <label className="field-block">
            <span>{copy.course}</span>
            <select
              className="text-input"
              value={selectedCourseId}
              onChange={(event) => setSelectedCourseId(event.target.value)}
            >
              <option value="">{copy.chooseCourse}</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </label>

          <label className="field-block">
            <span>{copy.mode}</span>
            <select
              className="text-input"
              value={selectedMode}
              onChange={(event) => setSelectedMode(event.target.value)}
            >
              <option value="pre">{copy.pre}</option>
              <option value="post">{copy.post}</option>
              <option value="both">{copy.both}</option>
            </select>
          </label>
        </div>

        {isLoadingCourses && <div className="empty-panel compact-panel state-panel state-panel--loading"><p>{copy.loading}</p></div>}
        {!isLoadingCourses && !courses.length && <div className="empty-panel compact-panel state-panel state-panel--empty"><p>{copy.noCourses}</p></div>}
        {!!pageError && <div className="empty-panel compact-panel state-panel state-panel--error"><p>{pageError}</p></div>}
      </section>

      <section className="panel-card">
        <h3>{copy.metricsTitle}</h3>
        <p className="muted-text">{selectedCourse?.title || copy.chooseCourse}</p>

        <div className="metric-grid trainer-overview-metric-grid trainer-overview-metric-grid--compact">
          <article className="metric-card trainer-overview-metric">
            <span>{copy.completeComparisons}</span>
            <strong>{formatNumber(comparison.compared_students || 0)}</strong>
          </article>
          <article className="metric-card trainer-overview-metric">
            <span>{copy.averagePre}</span>
            <strong>{formatNumber(comparison.average_pre_score)}</strong>
          </article>
          <article className="metric-card trainer-overview-metric">
            <span>{copy.averagePost}</span>
            <strong>{formatNumber(comparison.average_post_score)}</strong>
          </article>
          <article className="metric-card trainer-overview-metric">
            <span>{copy.averageGain}</span>
            <strong>{formatNumber(comparison.average_gain)}</strong>
          </article>
          <article className="metric-card trainer-overview-metric">
            <span>{copy.improvementRate}</span>
            <strong>{formatNumber(improvementRate)}%</strong>
          </article>
        </div>
      </section>

      <section className="panel-card">
        <h3>{copy.comparisonAverages}</h3>
        {comparison.compared_students ? (
          <div className="chart-panel">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={comparisonAverageChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="pre" name={copy.pre} fill="#2b6c8f" radius={[10, 10, 0, 0]} />
                <Bar dataKey="post" name={copy.post} fill="#2f855a" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="muted-text">{copy.noData}</p>
        )}
      </section>

      <section className="panel-card">
        <h3>{copy.comparisonOutcomes}</h3>
        {comparison.compared_students ? (
          <div className="chart-panel">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={comparisonOutcomeChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="improved" name={copy.improved} fill="#2f855a" radius={[10, 10, 0, 0]} />
                <Bar dataKey="unchanged" name={copy.unchanged} fill="#d69e2e" radius={[10, 10, 0, 0]} />
                <Bar dataKey="declined" name={copy.declined} fill="#c05621" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="muted-text">{copy.noData}</p>
        )}
      </section>

      <section className="panel-card">
        <h3>{copy.quickAnalysis}</h3>
        {comparison.compared_students ? (
          <div className="stack-sm">
            <p className="muted-text">
              {isArabic
                ? `نسبة التحسن الحالية ${formatNumber(improvementRate)}% من المتعلمين الذين أكملوا القبلي والبعدي، ومتوسط الدرجة انتقل من ${formatNumber(comparison.average_pre_score)} إلى ${formatNumber(comparison.average_post_score)}.`
                : `${formatNumber(improvementRate)}% of learners with complete pre/post data improved, and the average score moved from ${formatNumber(comparison.average_pre_score)} to ${formatNumber(comparison.average_post_score)}.`}
            </p>
            {bestAgeGroup && (
              <p className="muted-text">
                {isArabic
                  ? `${copy.ageInsightPrefix} ${bestAgeGroup.name} بمتوسط تحسن ${formatNumber(bestAgeGroup.average_gain)}.`
                  : `${copy.ageInsightPrefix} ${bestAgeGroup.name} with an average gain of ${formatNumber(bestAgeGroup.average_gain)}.`}
              </p>
            )}
            {bestGovernorate && (
              <p className="muted-text">
                {isArabic
                  ? `${copy.governorateInsightPrefix} ${bestGovernorate.name} بمتوسط تحسن ${formatNumber(bestGovernorate.average_gain)}.`
                  : `${copy.governorateInsightPrefix} ${bestGovernorate.name} with an average gain of ${formatNumber(bestGovernorate.average_gain)}.`}
              </p>
            )}
          </div>
        ) : (
          <p className="muted-text">{copy.noData}</p>
        )}
      </section>

      <section className="panel-card">
        <h3>{copy.quickSummary}</h3>
        {isLoadingReports ? (
          <div className="empty-panel compact-panel state-panel state-panel--loading">
            <p>{copy.loading}</p>
          </div>
        ) : courseReport ? (
          <div className="stack-md">
            <div className="trainer-overview-ai-caption">
              <strong>{courseReport.headline}</strong>
              <span>{copy.aiByline}</span>
            </div>
            <p>{courseReport.summary}</p>

            {!!courseReport.strengths?.length && (
              <div>
                <strong>{copy.strengths}</strong>
                <ul className="flat-list">
                  {courseReport.strengths.map((item, index) => (
                    <li key={`strength-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {!!courseReport.risks?.length && (
              <div>
                <strong>{copy.risks}</strong>
                <ul className="flat-list">
                  {courseReport.risks.map((item, index) => (
                    <li key={`risk-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {!!courseReport.recommendations?.length && (
              <div>
                <strong>{copy.recommendations}</strong>
                <ul className="flat-list">
                  {courseReport.recommendations.map((item, index) => (
                    <li key={`course-recommendation-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="muted-text">{copy.noData}</p>
        )}
      </section>
    </div>
  );
}

export default TrainerOverviewPage;
